import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { StyleSheet, Text, View } from './components/base_components';
import Breadcrumb from './components/breadcrumb';
import { BusyOverlay, useBusy } from './components/busy_component';
import BoxPropList from './components/box_prop_list';
import MediaGrantSelect from './components/media_grant_select';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';

import MediaGrantAdminStore from './stores/media_grant_admin_store';
import MediaGrantStore from './stores/media_grant_store';
import UserAdminStore from './stores/user_admin_store';
import { useUser, useOrg } from './stores/user_store';

const styles = StyleSheet.create({
  userEditScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  dialog: { marginLeft: '2rem', marginBottom: '2rem', flexDirection: 'column' },
  empty: {
    margin: '2rem',
    color: '#555',
    fontSize: '1.5rem',
    fontWeight: '600',
  },
  text: { marginTop: '1em', fontSize: '1.5em' },
  input: { alignSelf: 'stretch', maxWidth: '40rem', marginTop: '1rem' },
  button: { marginTop: '1rem', alignSelf: 'flex-start' },
  actionButton: { marginLeft: '1rem' },
  spacer: { flex: 1 },
});

export default function UserEditScreen(props) {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = useOrg();
  const user_id = parseInt(params?.user_id ?? '0', 10) || 0;
  const user = UserAdminStore.useUser(org?.org_id, user_id);
  const is_me = useUser()?.user_id === user_id;
  const [selected_grants, setSelectedGrants] = React.useState<number[]>([]);
  const [add_grant, setAddGrant] = React.useState(0);
  const [updates, setUpdates] = React.useState<any>({});
  const navigate = useNavigate();
  const org_id = org?.org_id;

  React.useEffect(() => {
    if (org_id) {
      UserAdminStore.fetch(org_id);
    }
  }, [org_id]);

  const selected_grant = user?.media_grant_list?.[selected_grants?.[0]];
  const selected_grant_id = selected_grant?.media_grant_id;

  function _onAddGrantPress() {
    if (add_grant && setBusy()) {
      const opts = { org_id, user_id, media_grant_id: add_grant };
      MediaGrantAdminStore.addUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to add grant, please try again.');
        } else {
          UserAdminStore.fetch(org_id);
          if (is_me) {
            MediaGrantStore.fetch(org_id);
          }
          setAddGrant(0);
        }
      });
    }
  }
  function _onDeleteGrantPress() {
    if (selected_grant_id && setBusy()) {
      const opts = { org_id, user_id, media_grant_id: selected_grant_id };
      MediaGrantAdminStore.removeUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to delete grant, please try again.');
        } else {
          UserAdminStore.fetch(org_id);
          if (is_me) {
            MediaGrantStore.fetch(org_id);
          }
          setSelectedGrants([]);
        }
      });
    }
  }
  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = { org_id, user_id };
      if (updates.is_admin !== undefined) {
        opts.is_admin = updates.is_admin === 'Admin';
      }
      if (updates.cap_add_device !== undefined) {
        opts.cap_add_device = updates.cap_add_device === 'Enabled';
      }
      if (updates.cap_web_download !== undefined) {
        opts.cap_web_download = updates.cap_web_download === 'Enabled';
      }
      if (updates.cap_web_upload !== undefined) {
        opts.cap_web_upload = updates.cap_web_upload === 'Enabled';
      }
      UserAdminStore.updateUser(opts, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to update user.');
        } else {
          complete?.();
        }
      });
    }
  }
  function _onRemovePress() {
    const confirmed = window.confirm(
      'Are you sure you want to remove this user?'
    );
    if (confirmed && setBusy()) {
      UserAdminStore.removeUserOrg({ org_id: org?.org_id, user_id }, (err) => {
        clearBusy();
        if (err) {
          window.alert('Failed to remove user.');
        } else {
          navigate(-1);
        }
      });
    }
  }

  const detail_buttons = (
    <>
      <TextButton
        style={styles.actionButton}
        type='danger'
        text='Remove User'
        onPress={_onRemovePress}
      />
      <View style={styles.spacer} />
    </>
  );
  const title = user?.user_name ?? user?.email;
  return (
    <View style={styles.userEditScreen}>
      <Breadcrumb list={[{ text: 'Users', url: '/admin/user' }, title]} />
      <TitleActionLine title={title} />
      <BoxPropList
        title='User Details'
        editButtons={detail_buttons}
        editable={!is_me}
        updates={updates}
        onChange={setUpdates}
        onSave={_onSave}
        columns={[
          [
            { label: 'Email', value: user?.email },
            { label: 'User Name', value: user?.user_name ?? '-' },
            {
              key: 'is_admin',
              label: 'Roles',
              value: user?.is_admin ? 'Admin' : 'Member',
              radio: { options: ['Admin', 'Member'] },
            },
          ],
          [
            {
              key: 'cap_web_download',
              label: 'Web Download',
              value: user?.cap_web_download ? 'Enabled' : 'Disabled',
              radio: {
                options: ['Enabled', 'Disabled'],
                disabled: user?.is_admin,
              },
            },
            {
              key: 'cap_web_upload',
              label: 'Web Upload',
              value: user?.cap_web_upload ? 'Enabled' : 'Disabled',
              radio: {
                options: ['Enabled', 'Disabled'],
                disabled: user?.is_admin,
              },
            },
            {
              key: 'cap_add_device',
              label: 'Device Add Permission',
              value: user?.cap_add_device ? 'Enabled' : 'Disabled',
              radio: {
                options: ['Enabled', 'Disabled'],
                disabled: user?.is_admin,
              },
            },
          ],
        ]}
      />
      <StandardBoxList
        title='Grants'
        detail={user?.media_grant_list?.length}
        headers={['Container Name', 'Volume Name', 'Prefix', 'Grant Status']}
        sizes={[2, 2, 2]}
        buttons={
          <>
            <TextButton
              style={styles.actionButton}
              text='Delete'
              disabled={selected_grants.length === 0}
              onPress={_onDeleteGrantPress}
            />
          </>
        }
        selected={selected_grants}
        singleSelect={true}
        onChange={setSelectedGrants}
        rows={user?.media_grant_list?.map?.((g: any) => ({
          list: [
            g.grant_name,
            g.volume_name,
            g.prefix_path,
            g.is_disabled ? 'Disabled' : 'Enabled',
          ],
        }))}
      >
        {user?.media_grant_list?.length === 0 ? (
          <Text style={styles.empty}>No Grants</Text>
        ) : null}
      </StandardBoxList>
      <StandardBoxList title='Add Grant'>
        <View style={styles.dialog}>
          <Text style={styles.text}>Add a grant to this user.</Text>
          <MediaGrantSelect
            style={styles.input}
            placeholder='Pick Grant'
            orgId={org?.org_id}
            isAdmin={org?.is_admin}
            excludeList={user?.media_grant_list}
            value={add_grant}
            onChange={setAddGrant}
          />
          <TextButton
            style={styles.button}
            text='Add Grant'
            onPress={_onAddGrantPress}
          />
        </View>
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
